import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormsMessage } from 'src/components/Common/@electron/FormsMessage';
import track from 'src/lib/Analytics';
import useQueryParam from 'src/lib/Hooks/useQueryParam';
import getCookies from 'src/lib/Utils/Cookies';
import setJurisdictionCookie from 'src/lib/Utils/setJurisdictionCookie';
import { formatVpvString } from '../FindContractor/machine';
import { getJson } from '../api/getJson';
import { getOffers } from './api/offers';
import Cards from './components/Cards';
import Header from './components/header';
import Jurisdiction, { isValidJurisdictionId } from './components/jurisdiction';
import NoOffers from './components/noOffers';
import OfferRebateModal from './components/offerRebateModal';
import filterData from './data';
import { PayloadType } from './examplePayload';

export type JurisdictionIdType =
  | 'NC01'
  | 'NC02'
  | 'SC01'
  | 'SC02'
  | 'FL01'
  | 'KY01'
  | 'OH01'
  | 'IN01';

export type ImagesType =
  | {
      'Heating and Air Conditioning Services': {
        card: string;
        icon: string;
      };
      'Insulation Services': {
        card: string;
        icon: string;
      };
      'Plumbing Services': {
        card: string;
        icon: string;
      };
      'Electrical Services': {
        card: string;
        icon: string;
      };
      'Pool Services': {
        card: string;
        icon: string;
      };
      'Solar Services': {
        card: string;
        icon: string;
      };
    }
  | undefined;

export type RebateOrOfferType =
  | {
      title: string;
      rebate: string;
      learnMore?: string;
      defaultMeasureCategory: string;
      description: string;
      details: string;
      img: string;
      type?: string;
      rebateAmount?: string;
      code?: string;
    }
  | undefined;

type DataType = {
  offers: RebateOrOfferType[];
  rebates: RebateOrOfferType[];
};

type ResolvedProps = ReturnType<typeof resolver>;

const resolver = (props: PayloadType) => {
  return {
    imagesUrl: props.fields.Images.value.href,
    getOffersUrl: props.fields.Endpoint.fields.Phrase.value,
    getRebatesUrl: props.fields.RebateData.value.href,
  };
};

const Main = (props: ResolvedProps) => {
  const [showModal, setShowModal] = useState(false);
  const [initialDataFetchFinished, setInitialDataFetchFinished] = useState(false);
  const [fetchedData, setFetchedData] = useState({ offers: null, rebates: null });
  const [jurisdictionId, setJurisdictionId] = useState<string | undefined>('');
  const [selectedOffer, setSelectedOffer] = useState<RebateOrOfferType>();
  const [areNoOffers, setNoOffers] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [images, setImages] = useState();
  const [data, setData] = useState<DataType>({ offers: [], rebates: [] });
  const [isError, setIsError] = useState(false);
  const [qpJurisdiction] = useQueryParam('jurisdiction');
  const [qpView] = useQueryParam('view');
  const offersRef = useRef<HTMLHeadingElement>(null);
  const rebatesRef = useRef<HTMLHeadingElement>(null);
  const history = useHistory();

  const closeModal = () => setShowModal(false);

  useEffect(() => {
    if (selectedOffer) {
      selectedOffer.title && setShowModal(!showModal);
    }
  }, [selectedOffer]);

  useEffect(() => {
    if (initialDataFetchFinished && qpView) {
      const lowercaseQPView = qpView.toLowerCase();
      if (lowercaseQPView === 'rebates') {
        rebatesRef.current?.scrollIntoView({ behavior: 'smooth' });
      } else if (lowercaseQPView === 'offers') {
        offersRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [initialDataFetchFinished]);

  useEffect(() => {
    if (qpJurisdiction) {
      let formattedQPJurisdiction = qpJurisdiction.toUpperCase();
      // appending 01 if query param doesn't include 01 or 02
      if (!formattedQPJurisdiction.includes('0')) {
        formattedQPJurisdiction = `${formattedQPJurisdiction}01`;
      }
      setJurisdictionId(formattedQPJurisdiction);
    } else {
      const cookieJurisdiction = getCookies()?.DEC ? JSON.parse(getCookies()?.DEC)?.JU : '';
      cookieJurisdiction && setJurisdictionId(cookieJurisdiction);
    }
  }, []);

  useEffect(() => {
    getJson(props.imagesUrl).then(images => {
      setImages(images);
    });
  }, []);

  useEffect(() => {
    setIsError(false);
    if (isValidJurisdictionId(jurisdictionId)) {
      jurisdictionId && setJurisdictionCookie(jurisdictionId);
      // clearing jurisdiction query param when jx cookie changes in case user clicks "Previous" on step3 of Find a Contractor, don't want to show old jx data
      qpJurisdiction && initialDataFetchFinished && history.replace({});
    }

    const isFlorida = jurisdictionId === 'FL01';

    if (isFlorida) {
      setNoOffers(true);
    }

    if (!isFlorida) {
      const makeRequest = async () => {
        // in case switching state from FL... need to setNoOffers(false) early so that it doesn't show while fetching data for new state
        areNoOffers && setNoOffers(false);
        try {
          let { offers, rebates } = fetchedData;
          if (!fetchedData.offers && !fetchedData.rebates) {
            [offers, rebates] = await Promise.all([
              getOffers(props.getOffersUrl),
              getJson(props.getRebatesUrl),
            ]);
            setFetchedData({ offers, rebates });
          }

          if (jurisdictionId) {
            const relatedOffers = filterData(offers, jurisdictionId);
            if (relatedOffers.length === 0) {
              setNoOffers(true);
            }
            const relatedRebates = rebates?.[jurisdictionId] || [];
            setData({ offers: relatedOffers, rebates: relatedRebates });
            !initialDataFetchFinished && setInitialDataFetchFinished(true);
          }
        } catch (err) {
          setIsError(true);
        }
      };

      makeRequest();
    }
  }, [jurisdictionId]);

  const returnBackToStates = (e: React.ChangeEvent) => {
    e.preventDefault();
    setJurisdictionId('');
    setSelectedState('');
  };

  const items = [
    {
      value: 'NC01',
      text: 'Duke Energy North Carolina',
    },
    {
      value: 'NC02',
      text: 'Duke Energy Progress North Carolina',
    },
    {
      value: 'NC',
      text: 'North Carolina',
    },
    {
      value: 'SC01',
      text: 'Duke Energy South Carolina',
    },
    {
      value: 'SC02',
      text: 'Duke Energy Progress South Carolina',
    },
    {
      value: 'SC',
      text: 'South Carolina',
    },
    {
      value: 'IN01',
      text: 'Indiana',
    },
    {
      value: 'KY01',
      text: 'Kentucky',
    },
  ];

  const hasRebates = data.rebates.length > 0;
  const hasOffers = data.offers.length > 0 && !areNoOffers;
  const shouldShowHeader = hasOffers && hasRebates;

  const offerSectionTitle = 'Special Offers';
  const rebateSectionTitle = 'Energy-Efficient Rebates';

  const sendModalAnalytics = (
    action: 'load' | 'clickthrough',
    modalTitle?: string,
    offerCode?: string
  ) => {
    const sectionTitle = offerCode ? offerSectionTitle : rebateSectionTitle;
    track({
      event: 'send-VPV',
      'vpv-name': formatVpvString(
        `/vpv/fid/modal/find-contractor/${action}/offers-rebates/${sectionTitle}/${
          modalTitle || 'na'
        }/${offerCode || 'na'}`
      ),
    });
  };

  return (
    <div id="fid-react" className="bg-gray-lighter flex flex-col flex-grow">
      <OfferRebateModal
        {...selectedOffer}
        images={images}
        {...{ showModal, closeModal, sendModalAnalytics }}
      />
      <Jurisdiction
        areNoOffers={areNoOffers}
        jurisdiction={jurisdictionId}
        setSelectedState={setSelectedState}
        selectedState={selectedState}
        setJurisdictionId={setJurisdictionId}
      />

      {areNoOffers && isValidJurisdictionId(jurisdictionId) && (
        <NoOffers returnBackToStates={returnBackToStates} jurisdiction={jurisdictionId} />
      )}

      {isError ? (
        <div className="flex flex-col grow justify-center self-center mt-48 mb-64">
          <FormsMessage
            variant="info"
            size="full"
            title="Unable to get offers and rebates."
            description="An Error Has Occurred."
          />
        </div>
      ) : (
        <>
          {jurisdictionId && (
            <div className="px-24">
              {shouldShowHeader && (
                <Header
                  title="Offers & Rebates"
                  description={`Exclusively for ${
                    items.find(element => element.value === jurisdictionId)?.text ||
                    'your jurisdiction'
                  } residential electric service customers. Find your match, become more energy efficient, and get money back!`}
                />
              )}
              {data.offers && !areNoOffers && (
                <Cards
                  type="offer"
                  offers={data.offers}
                  images={images}
                  scrollRef={offersRef}
                  title={offerSectionTitle}
                  setSelectedOffer={setSelectedOffer}
                  buttonsLabel="Get Offer"
                />
              )}
              {hasRebates && (
                <Cards
                  type="rebate"
                  offers={data.rebates}
                  scrollRef={rebatesRef}
                  images={images}
                  title={rebateSectionTitle}
                  setSelectedOffer={setSelectedOffer}
                  buttonsLabel="Rebate Details"
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const OffersAndRebates = (props: PayloadType) => {
  const content = resolver(props);
  return <Main {...content} />;
};

export default OffersAndRebates;
